import React from 'react';

import { format } from 'date-fns';

import Translate from 'src/app_deprecated/components/form/layout/Translate.react';

import { StateAndProvinceDropdown } from 'src/app/components/form-elements/dropdowns/state-province-dropdown';
import { DatePicker } from 'src/app/components/lib/date-picker';
import { InputAdornment } from 'src/app/components/lib/input';
import { MenuItem } from 'src/app/components/lib/menu-item';

import { Value, LabelContainer, BottomAlignedLabel, StyledSelect, StyledInput } from '../query-builder.styles';
import { type Variant } from '../use-query-builder';

import { BrandDropdown } from './brand-multiselect';
import { CustomerTypeDropdown } from './customer-type-multiselect';
import { LocationDropdown } from './location-multiselect';
import { ValueEnum } from './operators';
import { PricingTierDropdown } from './pricing-tier-multiselect';
import { ProductCategoryDropdown } from './product-category-multiselect';
import { StrainDropdown } from './strain-multiselect';
import { VendorDropdown } from './vendor-multiselect';

import type { Operator } from './operators';

export enum QueryBuilderUnit {
  day = 'day',
  dollar = 'dollar',
  grams = 'grams',
  milligrams = 'milligrams',
  percent = 'percent',
  quantity = 'qty',
  year = 'year',
}

type OperatorInputProps = {
  disabled?: boolean;
  index: number;
  onOperatorChange: (operator: string) => void;
  onValueChange: (value: string, secondValue?: boolean) => void;
  onVariantChange: (variant: string) => void;
  operator?: string;
  operators: Operator[];
  secondValue?: string | null;
  unit?: QueryBuilderUnit;
  value: string;
  variant?: string | null;
  variants: Variant[];
};

export function OperatorInput({
  disabled,
  index,
  onOperatorChange,
  onValueChange,
  onVariantChange,
  operator,
  secondValue,
  unit,
  value,
  variant = '',
  variants,
  operators,
}: OperatorInputProps) {
  const endInputAdornment = (unit: QueryBuilderUnit | undefined) => {
    const unitMap = {
      [QueryBuilderUnit.day]: 'days',
      [QueryBuilderUnit.quantity]: 'qty',
      [QueryBuilderUnit.year]: 'years',
      [QueryBuilderUnit.percent]: '%',
      [QueryBuilderUnit.milligrams]: 'mg',
      [QueryBuilderUnit.grams]: 'g',
    };

    const label = unit ? unitMap[unit] : undefined;

    return label ? <InputAdornment position='end'>{label}</InputAdornment> : null;
  };

  const startInputAdornment = (unit: QueryBuilderUnit | undefined) => {
    const unitMap = {
      [QueryBuilderUnit.dollar]: '$',
    };

    const label = unit ? unitMap[unit] : undefined;

    return label ? <InputAdornment position='start'>{label}</InputAdornment> : null;
  };

  const renderInput = (operator) => {
    if (!operator) {
      return null;
    }

    const startAdornment = startInputAdornment(unit);
    const endAdornment = endInputAdornment(unit);

    switch (operator.valueType) {
      case ValueEnum.Boolean:
        return (
          <StyledSelect
            disabled={disabled}
            paddingRightInput='42px'
            placeholder='select a value'
            sx={{ minWidth: '100px' }}
            value={value}
            onChange={({ target }) => onValueChange(target.value)}
          >
            <MenuItem value='1'>true</MenuItem>
            <MenuItem value='0'>false</MenuItem>
          </StyledSelect>
        );
      case ValueEnum.Date:
        return (
          <DatePicker
            allowPastDate
            date={value}
            disableClearDate
            disabled={disabled}
            key='date'
            placeholder='select a date'
            onDateChange={(newDate) => {
              const formattedDate = format(newDate, 'yyyy-MM-dd');
              onValueChange(formattedDate);
            }}
          />
        );
      case ValueEnum.Percent:
        return (
          <>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              type='number'
              value={value}
              onChange={({ target }) => onValueChange(target.value)}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.Number:
      case ValueEnum.NetWeight:
        return (
          <>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              startAdornment={startAdornment}
              type='number'
              value={value}
              onChange={({ target }) => onValueChange(String(target.value))}
            />
            {variants?.length > 0 && (
              <StyledSelect
                automationId={`query-builder_select-variant_select-${String(index)}`}
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(String(target.value))}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.NumberRange:
        return (
          <>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              key='number-range-start'
              startAdornment={startAdornment}
              type='number'
              value={value}
              onChange={({ target }) => onValueChange(String(target.value))}
            />
            <LabelContainer minWidth='14px'>
              <BottomAlignedLabel>
                <Translate>to</Translate>
              </BottomAlignedLabel>
            </LabelContainer>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              key='number-range-end'
              startAdornment={startAdornment}
              type='number'
              value={secondValue ?? ''}
              onChange={({ target }) => onValueChange(String(target.value), true)}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.DateRange:
        return (
          <>
            <DatePicker
              allowPastDate
              date={value}
              disableClearDate
              disabled={disabled}
              key='date-range-start'
              placeholder='select a date'
              onDateChange={(newDate) => {
                const formattedDate = format(newDate, 'yyyy-MM-dd');
                onValueChange(formattedDate);
              }}
            />
            <LabelContainer minWidth='22px'>
              <BottomAlignedLabel>
                <Translate>and</Translate>
              </BottomAlignedLabel>
            </LabelContainer>
            <DatePicker
              allowPastDate
              date={secondValue ?? ''}
              disableClearDate
              disabled={disabled}
              key='date-range-end'
              placeholder='select a date'
              onDateChange={(newDate) => {
                const formattedDate = format(newDate, 'yyyy-MM-dd');
                onValueChange(formattedDate, true);
              }}
            />
          </>
        );
      case ValueEnum.StateLocation:
        return (
          <StateAndProvinceDropdown
            disabled={disabled}
            placeholder='select a state/province'
            sx={{ minWidth: '300px' }}
            value={value}
            onChange={(state) => {
              onValueChange(state);
            }}
          />
        );
      case ValueEnum.ProductCategory:
        return (
          <ProductCategoryDropdown
            disabled={disabled}
            values={value}
            onChange={(categoryOptions) => {
              // here we're setting the array to a string
              onValueChange(String(categoryOptions));
            }}
          />
        );
      case ValueEnum.Brand:
        return (
          <BrandDropdown
            disabled={disabled}
            values={value}
            onChange={(brandOptions) => {
              onValueChange(String(brandOptions));
            }}
          />
        );
      case ValueEnum.Location:
        return (
          <LocationDropdown
            disabled={disabled}
            values={value}
            onChange={(locationOptions) => {
              onValueChange(String(locationOptions));
            }}
          />
        );
      case ValueEnum.CustomerType:
        return (
          <CustomerTypeDropdown
            automationId={`query-builder_customer-type-multi-select-dropdown-${String(index)}`}
            disabled={disabled}
            values={value}
            onChange={(customerTypeOptions) => {
              onValueChange(String(customerTypeOptions));
            }}
          />
        );
      case ValueEnum.Vendor:
        return (
          <VendorDropdown
            disabled={disabled}
            values={value}
            onChange={(vendorOptions) => {
              onValueChange(String(vendorOptions));
            }}
          />
        );
      case ValueEnum.Strain:
        return (
          <StrainDropdown
            disabled={disabled}
            values={value}
            onChange={(strainOptions) => {
              onValueChange(String(strainOptions));
            }}
          />
        );
      case ValueEnum.PricingTier:
        return (
          <PricingTierDropdown
            disabled={disabled}
            values={value}
            onChange={(pricingTierOptions) => {
              onValueChange(String(pricingTierOptions));
            }}
          />
        );
      case ValueEnum.String:
      default:
        return (
          <StyledInput
            disabled={disabled}
            key='string'
            type='text'
            value={value}
            onChange={({ target }) => onValueChange(target.value)}
          />
        );
    }
  };

  const selectedOperator = operators.find((op) => op.name === operator);

  return (
    <>
      <StyledSelect
        automationId={`query-builder_select-operator_select-${String(index)}`}
        disabled={disabled || selectedOperator?.valueType === ValueEnum.Boolean}
        paddingRightInput='42px'
        placeholder='select an option'
        value={operator ?? ''}
        onChange={({ target }) => onOperatorChange(target.value)}
      >
        {operators.map((operator) => (
          <MenuItem key={operator.name} value={operator.name}>
            {operator.label}
          </MenuItem>
        ))}
      </StyledSelect>
      {operator && (
        <Value data-testid={`query-builder_input-value_input-${String(index)}`}>{renderInput(selectedOperator)}</Value>
      )}
    </>
  );
}
